import { useEffect, useRef, useState } from 'react';

const getScrollPosition = () =>
  Math.max(
    0,
    window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0,
  );

function useSmartScrollVisibility({
  disabled = false,
  hideAfter = 96,
  directionThreshold = 12,
  resetKey,
} = {}) {
  const [isVisible, setIsVisible] = useState(true);
  const isVisibleRef = useRef(true);
  const lastScrollPositionRef = useRef(0);
  const accumulatedDeltaRef = useRef(0);
  const animationFrameRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateVisibility = (nextVisibility) => {
      if (isVisibleRef.current === nextVisibility) {
        return;
      }

      isVisibleRef.current = nextVisibility;
      setIsVisible(nextVisibility);
    };

    lastScrollPositionRef.current = getScrollPosition();
    accumulatedDeltaRef.current = 0;
    updateVisibility(true);

    if (disabled) {
      return undefined;
    }

    const evaluateScroll = () => {
      animationFrameRef.current = 0;
      const currentScrollPosition = getScrollPosition();
      const scrollDelta = currentScrollPosition - lastScrollPositionRef.current;
      lastScrollPositionRef.current = currentScrollPosition;

      if (currentScrollPosition <= hideAfter) {
        accumulatedDeltaRef.current = 0;
        updateVisibility(true);
        return;
      }

      if (Math.abs(scrollDelta) < 1) {
        return;
      }

      if (
        accumulatedDeltaRef.current === 0 ||
        Math.sign(accumulatedDeltaRef.current) === Math.sign(scrollDelta)
      ) {
        accumulatedDeltaRef.current += scrollDelta;
      } else {
        accumulatedDeltaRef.current = scrollDelta;
      }

      if (accumulatedDeltaRef.current >= directionThreshold) {
        accumulatedDeltaRef.current = 0;
        updateVisibility(false);
      } else if (accumulatedDeltaRef.current <= -directionThreshold) {
        accumulatedDeltaRef.current = 0;
        updateVisibility(true);
      }
    };

    const handleScroll = () => {
      if (animationFrameRef.current) {
        return;
      }

      if (typeof window.requestAnimationFrame === 'function') {
        animationFrameRef.current = window.requestAnimationFrame(evaluateScroll);
      } else {
        evaluateScroll();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);

      if (animationFrameRef.current && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = 0;
    };
  }, [directionThreshold, disabled, hideAfter, resetKey]);

  return isVisible;
}

export default useSmartScrollVisibility;
